import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Save, Upload, Image as ImageIcon, Megaphone, Eye } from "lucide-react";
import api, { formatErr } from "../../lib/api";
import { uploadImage } from "../../lib/uploads";

export default function AdmPopup() {
  const [s, setS] = useState({ popup_enabled: false, popup_title: "", popup_body: "", popup_code: "", popup_image: "" });
  const [busy, setBusy] = useState(false);
  const [previewing, setPreviewing] = useState(false);

  useEffect(() => { api.get("/admin/popup").then((r) => setS(r.data || {})); }, []);

  const save = async () => {
    setBusy(true);
    try {
      const r = await api.put("/admin/popup", s);
      setS(r.data); toast.success("Popup saved — refresh storefront to preview to first-time visitors.");
    } catch (e) { toast.error(formatErr(e)); }
    finally { setBusy(false); }
  };

  const handleFile = async (e) => {
    const f = e.target.files?.[0]; if (!f) return;
    if (f.size > 4 * 1024 * 1024) return toast.error("Image must be < 4 MB");
    try {
      const r = await uploadImage(f, "popup");
      setS({ ...s, popup_image: r.url });
      toast.success(r.storage === "s3" ? "Uploaded to S3 — Save to apply." : "Loaded — Save to apply.");
    } catch (err) { toast.error(formatErr(err)); }
  };

  return (
    <div className="space-y-6">
      <div className="flex justify-between items-end gap-3 flex-wrap">
        <div>
          <div className="overline mb-2">Marketing</div>
          <h1 className="font-display text-4xl text-white">Discount Popup</h1>
          <p className="text-sm text-white/55 mt-2 max-w-2xl">First-time visitors see this popup on the storefront. Use it to highlight an active offer, a welcome discount, or a limited-time deal.</p>
        </div>
        <button data-testid="popup-preview" onClick={() => setPreviewing(true)} className="btn-outline text-xs"><Eye size={12}/> Preview</button>
      </div>

      <div className="grid lg:grid-cols-[1fr_1fr] gap-6">
        <div className="card-luxe p-6 space-y-4">
          <label className="flex items-center gap-2 text-sm text-white/85">
            <input data-testid="popup-enabled" type="checkbox" checked={!!s.popup_enabled} onChange={(e) => setS({ ...s, popup_enabled: e.target.checked })}/>
            Show this popup on first visit
          </label>

          <div>
            <div className="text-[11px] uppercase tracking-luxe text-white/40 mb-1">Title</div>
            <input data-testid="popup-title" className="input-box" value={s.popup_title || ""} onChange={(e) => setS({ ...s, popup_title: e.target.value })} placeholder="Welcome to Luxe Watches"/>
          </div>
          <div>
            <div className="text-[11px] uppercase tracking-luxe text-white/40 mb-1">Body</div>
            <textarea data-testid="popup-body" className="input-box" rows={3} value={s.popup_body || ""} onChange={(e) => setS({ ...s, popup_body: e.target.value })} placeholder="Enjoy 10% off your first order. Use code WELCOME10 at checkout."/>
          </div>
          <div>
            <div className="text-[11px] uppercase tracking-luxe text-white/40 mb-1">Discount Code</div>
            <input data-testid="popup-code" className="input-box font-mono" value={s.popup_code || ""} onChange={(e) => setS({ ...s, popup_code: e.target.value })} placeholder="WELCOME10"/>
          </div>

          <div>
            <div className="text-[11px] uppercase tracking-luxe text-white/40 mb-1">Banner Image (optional)</div>
            <div className="flex items-center gap-3">
              {s.popup_image ? (
                <div className="h-20 w-32 bg-[#070707] border border-white/10 overflow-hidden">
                  <img src={s.popup_image} alt="" className="w-full h-full object-cover"/>
                </div>
              ) : (
                <div className="h-20 w-32 bg-white/5 border border-dashed border-white/15 flex items-center justify-center text-white/30"><ImageIcon size={16}/></div>
              )}
              <label data-testid="popup-upload" className="cursor-pointer flex items-center gap-1.5 px-3 py-2 border border-white/15 hover:border-copper text-[11px] uppercase tracking-luxe text-white/70 hover:text-white">
                <Upload size={12}/> Upload
                <input type="file" accept="image/*" className="hidden" onChange={handleFile}/>
              </label>
              {s.popup_image && <button onClick={() => setS({ ...s, popup_image: "" })} className="text-[11px] uppercase tracking-luxe text-red-400 hover:text-red-300">Clear</button>}
            </div>
            <input className="input-box mt-2" placeholder="Or paste an image URL" value={s.popup_image || ""} onChange={(e) => setS({ ...s, popup_image: e.target.value })}/>
          </div>

          <button data-testid="popup-save" onClick={save} disabled={busy} className="btn-primary"><Save size={13}/> {busy ? "Saving…" : "Save Popup"}</button>
        </div>

        {/* Inline preview card */}
        <div className="card-luxe p-6">
          <div className="overline mb-3 flex items-center gap-2"><Megaphone size={13} className="text-copper"/> Live Preview</div>
          <div className="bg-white text-dark border border-black/10 p-6">
            {s.popup_image && <img src={s.popup_image} alt="" className="w-full h-40 object-cover mb-4"/>}
            <div className="font-display text-2xl text-dark">{s.popup_title || "Your popup title"}</div>
            <p className="text-sm text-gray-700 mt-3 whitespace-pre-line">{s.popup_body || "Your popup body text will appear here…"}</p>
            {s.popup_code && (
              <div className="mt-4 inline-block bg-[#FAF7F2] border border-copper/30 px-4 py-2 font-mono text-copper text-sm">{s.popup_code}</div>
            )}
            <div className="mt-5 text-[10px] uppercase tracking-luxe text-gray-500">Preview · {s.popup_enabled ? "active" : "hidden from visitors"}</div>
          </div>
          <div className="mt-3 text-[11px] text-white/40 leading-relaxed">
            The popup is shown only on a visitor's first session — once they close it, it stays dismissed for that browser. Update copy any time without affecting people who've already seen it.
          </div>
        </div>
      </div>

      {/* Full-screen modal-style preview */}
      {previewing && (
        <div data-testid="popup-modal-preview" className="fixed inset-0 z-50 flex items-center justify-center p-4 maintenance-blur" onClick={() => setPreviewing(false)}>
          <div className="bg-white max-w-md w-full text-dark p-8" onClick={(e) => e.stopPropagation()}>
            {s.popup_image && <img src={s.popup_image} alt="" className="w-full h-48 object-cover mb-5"/>}
            <div className="overline text-copper mb-2">Limited Offer</div>
            <div className="font-display text-3xl">{s.popup_title || "Your popup title"}</div>
            <p className="text-gray-700 mt-3 whitespace-pre-line">{s.popup_body}</p>
            {s.popup_code && <div className="mt-5 inline-block bg-[#FAF7F2] border border-copper/30 px-5 py-3 font-mono text-copper">{s.popup_code}</div>}
            <button onClick={() => setPreviewing(false)} className="btn-primary mt-6">Close Preview</button>
          </div>
        </div>
      )}
    </div>
  );
}
